import React from "react";
import {
    Drawer, DrawerContent, DrawerHeader, DrawerTitle,
    DrawerDescription, DrawerBody, Divider, Text, Stack, Label
} from "@nike/podium";
import {Check} from "@nike/nike-design-system-icons";
import {ActionedRecord} from "../../common/data/types";

type FlowInfoPanelProps = {
    code: string | null;
    actionedCodes: Record<string, ActionedRecord>;
    onClose: () => void;
};

export function FlowInfoPanel({code, actionedCodes, onClose}: FlowInfoPanelProps) {
    const actioned = code ? actionedCodes[code] : null;
    const isOpen = code !== null && actioned !== null;
    const isProcessed = actioned?.status === 'processed';

    if (!actioned) return null;

    const typeLabel = actioned.adjType === 'POSADJ'
        ? 'Goods entry (POSADJ)'
        : 'Goods removal (NEGADJ)';
    const triggerLabel = actioned.adjType === 'POSADJ'
        ? 'Positive adjustment (Customs Inventory)'
        : 'Negative adjustment (Customs Inventory)';
    const detailLabel = actioned.bucket === 'under'
        ? 'ADJALL – bulk'
        : 'CLEANUP – bulk';

    return (
        <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DrawerContent width="340px">
                <DrawerHeader>
                    <DrawerTitle>Flow information</DrawerTitle>
                    <DrawerDescription>{code}</DrawerDescription>
                </DrawerHeader>
                <DrawerBody>
                    <Stack gap="m">
                        <Stack gap="xs">
                            <Label font="body-4" className="im-fp-label">Status</Label>
                            <div>
                                {isProcessed ? (
                                    <span className="im-fp-status-processed">
                                        <Check size="s" />
                                        Processed
                                    </span>
                                ) : (
                                    <span className="im-fp-status-processing">
                                        <span className="im-cs-processing-dot" />
                                        Processing
                                    </span>
                                )}
                            </div>
                        </Stack>
                        <Divider />
                        <Stack gap="xs">
                            <Label font="body-4" className="im-fp-label">Type</Label>
                            <Text font="body-3" className="im-fp-value">{typeLabel}</Text>
                        </Stack>
                        <Stack gap="xs">
                            <Label font="body-4" className="im-fp-label">Trigger</Label>
                            <Text font="body-3" className="im-fp-value">{triggerLabel}</Text>
                        </Stack>
                        <Stack gap="xs">
                            <Label font="body-4" className="im-fp-label">Detail</Label>
                            <Text font="body-3" className="im-fp-value">{detailLabel}</Text>
                        </Stack>
                        <Stack gap="xs">
                            <Label font="body-4" className="im-fp-label">LC routing</Label>
                            <Text font="body-3" className="im-fp-value">{actioned.lc}</Text>
                        </Stack>
                        <Stack gap="xs">
                            <Label font="body-4" className="im-fp-label">Persistent imbalance units</Label>
                            <Text font="body-3" className="im-fp-value">{Math.abs(actioned.units)} units</Text>
                        </Stack>
                        <Divider />
                        <Stack gap="xs">
                            <Label font="body-4" className="im-fp-label">Business reference</Label>
                            <Text font="body-3" className="im-fp-value mono">
                                {isProcessed ? (actioned.ref || '—') : <Text font="body-4" as="span" color="secondary">Pending…</Text>}
                            </Text>
                        </Stack>
                        <Stack gap="xs">
                            <Label font="body-4" className="im-fp-label">Processed at</Label>
                            <Text font="body-3" className="im-fp-value mono">
                                {isProcessed ? (actioned.ts || '—') : '—'}
                            </Text>
                        </Stack>
                    </Stack>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}
